import React from 'react';
import { filesize } from 'filesize';

import { apiCallWithOptions } from '~/_base/functions/apicalls';
import Policy from '~/_base/classes/policy';

import RailsNotification from '~/_base/components/railsNotification';

import { Button, Paper, Typography, Collapse, Grid } from '@mui/material';
import { IWarning } from '~/_base/components/helixIcons';
import Cookie from 'js-cookie';

import MaterialFormGrid, {
  MaterialFormGridControl,
  MaterialFormGridCollapse,
} from '~/_base/components/materialFormGrid';
import styles from '../../stylesheets/_sendForm.scss';
import { t } from 'i18next';

const attachmentWarningLimit = 15 * 1024 * 1024; //15mb

export default class AppSendEmail extends React.Component {
  constructor(props) {
    super();
    this.emailList = this.loadEmailsForAutoComplete();

    this.state = {
      root: document.location.pathname.replace(/\/$/gm, ''),
      showCcBcc: false,
      title: null,
      sendEvent: null,
      saving: false,
      dirty: false,
    };

    apiCallWithOptions(
      `${document.location.pathname.replace(/\/$/gm, '')}.json`,
      'GET'
    ).then(sendEventResponse => {
      this.setState({
        title: sendEventResponse.title,
        sendEvent: Policy.fold(
          sendEventResponse.event,
          sendEventResponse.policy
        ),
      });
    });
  }

  updateSendEvent(action) {
    Object.keys(action).forEach(
      prop => (this.state.sendEvent[prop] = action[prop])
    );
    this.setState({ dirty: true, sendEvent: this.state.sendEvent });
  }

  updateAttachment(id, action) {
    this.state.sendEvent.email_attachments =
      this.state.sendEvent.email_attachments || {};
    let attachment = this.state.sendEvent.email_attachments.attachments.find(
      item => item.blob_file_id == id
    );
    Object.keys(action).forEach(prop => (attachment[prop] = action[prop]));
    this.setState({ dirty: true, sendEvent: this.state.sendEvent });
  }

  updateBaseForm(id, action) {
    this.state.sendEvent.email_attachments =
      this.state.sendEvent.email_attachments || {};
    let baseForm = this.state.sendEvent.email_attachments.linked_base_forms.find(
      item => item.base_form_id == id
    );
    Object.keys(action).forEach(prop => (baseForm[prop] = action[prop]));
    this.setState({ dirty: true, sendEvent: this.state.sendEvent });
  }

  render() {
    let { title, sendEvent, showCcBcc } = this.state;
    if (sendEvent == null) {
      return null;
    }
    Policy.validate(sendEvent);

    // dedeup the file attachments based on the filename / size combination
    let emailAttachments = sendEvent.email_attachments || {};
    let emailAttachmentsHash = {};
    Object.keys(emailAttachments).length > 0 &&
      emailAttachments.attachments.map(
        a => (emailAttachmentsHash[`${a.name}:${a.size}`] = a)
      );
    emailAttachments.attachments = Object.values(emailAttachmentsHash);

    let totalAttachments = (emailAttachments.attachments || [])
      .filter(attachment => attachment.send === true)
      .reduce((sum, value) => sum + value.size, 0);
    let showAttachmentsWarning = totalAttachments > attachmentWarningLimit;

    // the DOM has to be set up in a certain way so that the autocomplete can work. 1) The inputs need to be
    // inside a <form> element 2) the prop needs to have the word email in it. In order to stop the CC and
    // BCC fields getting filled out with the same information, we set role="presentation" (just setting
    // autocomplete="off" is not enough)
    return (
      <div style={{ margin: '0 auto', maxWidth: '900px' }}>
        <RailsNotification />
        <div style={{ margin: '0 1em' }}>
          <p />
          <Typography variant="h6">{title}</Typography>
          <p />
          <Paper style={{ padding: '0.5em' }}>
            <MaterialFormGrid
              source={sendEvent}
              onChange={(prop, val) => this.updateSendEvent({ [prop]: val })}
            >
              <MaterialFormGridControl
                size={12}
                label={t('To')}
                prop="to_email_addresses"
                type="email"
                autoComplete="off"
                action={() => this.toggleCcBcc()}
                actionLabel={t('Cc/Bcc')}
              />

              <EmailAutoCompleter
                emailList={this.emailList}
                emailText={
                  this.state.sendEvent &&
                  this.state.sendEvent.to_email_addresses
                }
                onSelect={email =>
                  this.autoCompleteClick(email, 'to_email_addresses')
                }
              />

              <MaterialFormGridCollapse in={showCcBcc}>
                <MaterialFormGridControl
                  size={12}
                  label={t('Cc')}
                  prop="cc_email_addresses"
                  autoComplete="off"
                  role="presentation"
                />

                <EmailAutoCompleter
                  emailList={this.emailList}
                  emailText={
                    this.state.sendEvent &&
                    this.state.sendEvent.cc_email_addresses
                  }
                  onSelect={email =>
                    this.autoCompleteClick(email, 'cc_email_addresses')
                  }
                />

                <MaterialFormGridControl
                  size={12}
                  label={t('Bcc')}
                  prop="bcc_email_addresses"
                  autoComplete="off"
                  role="presentation"
                />

                <EmailAutoCompleter
                  emailList={this.emailList}
                  emailText={
                    this.state.sendEvent &&
                    this.state.sendEvent.bcc_email_addresses
                  }
                  onSelect={email =>
                    this.autoCompleteClick(email, 'bcc_email_addresses')
                  }
                />
              </MaterialFormGridCollapse>
              <MaterialFormGridControl
                size={12}
                label={t('Subject')}
                prop="subject"
              />
              <MaterialFormGridControl
                size={12}
                label={t('Message')}
                prop="body"
                multiline={true}
              />
              <MaterialFormGridControl
                size={12}
                label={t('Bcc yourself')}
                prop="bcc_sender"
              />
              {this.renderLinkedBaseForms(emailAttachments.linked_base_forms)}
              {this.renderAttachments(emailAttachments.attachments)}
            </MaterialFormGrid>
          </Paper>
          <Collapse in={showAttachmentsWarning}>
            <p />
            <Paper
              style={{
                padding: '1em',
                backgroundColor: '#ffa000',
              }}
            >
              <Typography
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                }}
              >
                <IWarning style={{ marginRight: '1em' }} />{' '}
                {t(
                  'Total attachments greater than {{fileSize}}. This email may be rejected by the recipient(s) email server.',
                  { fileSize: filesize(attachmentWarningLimit, { round: 0 }) }
                )}
              </Typography>
            </Paper>
          </Collapse>

          <p style={{ textAlign: 'right' }}>
            <Button onClick={() => this.cancel()}>{t('Cancel')}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => this.save()}
            >
              {t('Send')}
            </Button>
          </p>
        </div>
      </div>
    );
  }

  // append the new email address to the end of the list
  autoCompleteClick(email, propName) {
    const emailText = this.state.sendEvent && this.state.sendEvent[propName];
    const emailList = (emailText || '').split(',').map(a => a.trim());
    emailList[emailList.length - 1] = email;
    const emailString = emailList.join(', ');

    const sendEvent = this.state.sendEvent || {};
    sendEvent[propName] = emailString;
    this.setState({ sendEvent: sendEvent });
  }

  loadEmailsForAutoComplete() {
    try {
      return JSON.parse(Cookie.get('ape-recent-emails')) || [];
    } catch (e) {
      return [];
    }
  }

  // will take the list of recent emails, make sure it is less than some length, then move
  // the current 'to' emails to the top of the list and it to a cookie
  saveEmailsForAutoComplete() {
    // helper to reduce an array to the distinct values without taking case into account
    // but still saving the case of whatever matches. Do this by creating a dict with the
    // lowercased as the key and the cased version as the value
    const nocaseDistinct = arr => {
      const hash = {};
      arr.forEach(v => (hash[(v || '').toLowerCase()] = v));
      return Object.values(hash);
    };

    const propsList = [
      'bcc_email_addresses',
      'cc_email_addresses',
      'to_email_addresses',
    ];

    // we take the email text from each of the fields and merge the emails into a single
    // array, to save in the autocomplete email list
    const currentEmails = propsList
      .map(prop => this.state.sendEvent && this.state.sendEvent[prop])
      .map(emailText => (emailText || '').split(',').map(a => a.trim()))
      .reduce((list, emails) => list.concat(emails), []);

    const lowerEmails = currentEmails.map(email => email.toLowerCase());

    // we want the new emails to be added to the top of the autocomplete list, even if they exist
    // already in there. Do this by first filtering out all the new emails from the existing list
    // and then insert them at the start of the array. (then convert to json and save to the cookie)
    //
    // need to make sure the filtering is done case insensitive, but that the email *is* added to the
    // list with the case
    this.emailList = this.emailList
      .filter(a => !lowerEmails.includes(a.toLowerCase()))
      .slice(0, 32);
    currentEmails.forEach(a => this.emailList.unshift(a)); // push to start of array
    this.emailList = nocaseDistinct(this.emailList);
    Cookie.set('ape-recent-emails', JSON.stringify(this.emailList), {
      expires: 365,
    });
  }

  renderLinkedBaseForms(baseForms) {
    if (baseForms == null || baseForms.length == 0) {
      return null;
    }
    return (
      <Grid size={12}>
        <Typography variant="caption">{t('Linked Forms')}</Typography>
        {baseForms.map(baseForm => this.renderLinkedBaseForm(baseForm))}
      </Grid>
    );
  }

  renderLinkedBaseForm(baseForm) {
    return (
      <MaterialFormGrid
        key={baseForm.base_form_id}
        spacing={0}
        source={baseForm}
        onChange={(prop, val) =>
          this.updateBaseForm(baseForm.base_form_id, { [prop]: val })
        }
      >
        <MaterialFormGridControl size={12} label={baseForm.name} prop="send" />
      </MaterialFormGrid>
    );
  }

  renderAttachments(attachments) {
    if (attachments == null || attachments.length == 0) {
      return null;
    }
    return (
      <Grid size={12}>
        <Typography variant="caption">{t('Attachments')}</Typography>
        {attachments.map(attachment => this.renderAttachment(attachment))}
      </Grid>
    );
  }

  renderAttachment(attachment) {
    return (
      <MaterialFormGrid
        key={attachment.blob_file_id}
        spacing={0}
        source={attachment}
        onChange={(prop, val) =>
          this.updateAttachment(attachment.blob_file_id, {
            [prop]: val,
          })
        }
      >
        <MaterialFormGridControl
          size={12}
          label={`${attachment.name} (${filesize(attachment.size || 0)})`}
          prop="send"
        />
      </MaterialFormGrid>
    );
  }

  toggleCcBcc() {
    this.setState({ showCcBcc: !this.state.showCcBcc });
  }

  getSubmittable(state) {
    let submittable = JSON.parse(JSON.stringify(state.sendEvent));
    return Policy.unfold(submittable);
  }

  save() {
    let errors = Policy.validate(this.state.sendEvent);
    if (errors.length > 0) {
      window.showAlert(t('Please fix errors on page before sending.'), 'error');
      return;
    }

    this.saveEmailsForAutoComplete();

    let submittable = this.getSubmittable(this.state);
    let uri = `${document.location.pathname.replace('/new', '')}.json`;
    let method = 'POST';
    this.setState({ saving: true }, () => {
      apiCallWithOptions(uri, method, {
        body: { send_event: submittable },
      })
        .then(createResponse => {
          document.location = createResponse.redirect;
        })
        .catch(err => {
          window.showAlert(err.message, 'error');
        });
    });
  }

  cancel() {
    if (
      this.state.dirty === false ||
      confirm(t('Are you sure you want to leave this page and lose changes?'))
    ) {
      let rootPath = document.location.pathname
        .split('/')
        .slice(0, 2)
        .join('/');
      document.location = rootPath + document.location.search;
    }
  }
}

class EmailAutoCompleter extends React.Component {
  render() {
    return (
      <div className="auto-email-list">
        {this.matchingEmails.map(email => (
          <a
            key={email}
            href="#"
            className="auto-email-item"
            onClick={e => this.props.onSelect(email)}
          >
            {email}
          </a>
        ))}
      </div>
    );
  }

  get matchingEmails() {
    const prefix = this.currentToEmail().toLowerCase();

    return this.props.emailList
      .filter(
        email => email.toLowerCase().startsWith(prefix) && prefix != email
      )
      .slice(0, 4);
  }

  currentToEmails() {
    return (this.props.emailText || '').split(',').map(a => a.trim());
  }

  // returns the current last email
  currentToEmail() {
    const emails = this.currentToEmails();
    return (emails && emails[emails.length - 1]) || '';
  }
}
